import { ChangeDetectionStrategy, Component, computed, inject, resource, output } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  EventqDataTable,
  EventqDataTableColumn,
  EventqDataTableRow,
  EventqLinedTab,
  EventqSortState,
} from '@ecam/eventq-components';
import { environment } from '../../../../../environments/environment';
import { BrowserTabService } from '../../../../core/services/browser-tab.service';
import { AgentDataSyncService } from '../../../../core/services/agent-data-sync.service';
import { EventHandleService } from '../../../../core/services/event-handle.service';
import { autoReloadResource } from '../../../../core/utils/auto-reload-resource';
import { withPreviousResourceValue } from '../../../../core/utils/with-previous-resource-value';
import { activeQueueSort, queueTableColumn } from '../../queue-table-sort';
import { EventsDashboardService } from '../../services/events-dashboard.service';
import { displayQueuePriority, isRequeuedAfterPark } from '../../utils/queue-priority.util';

@Component({
  selector: 'app-events-queue-section',
  imports: [EventqLinedTab, EventqDataTable],
  templateUrl: './events-queue-section.html',
  styleUrl: './events-queue-section.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventsQueueSection {
  private readonly eventsService = inject(EventsDashboardService);
  private readonly browserTabs = inject(BrowserTabService);
  private readonly eventHandles = inject(EventHandleService);
  private readonly dataSync = inject(AgentDataSyncService);

  readonly eventRowActivate = output<string>();

  readonly sort = computed(() => activeQueueSort(this.browserTabs.eventsSort()));
  readonly selectedRowId = computed(() => this.browserTabs.selectedEventId());
  readonly disabledRowIds = computed(() => this.eventHandles.blockedOpenEventIds());

  readonly columns: EventqDataTableColumn[] = [
    { key: 'requeuedIndicator', label: '', width: '32px', align: 'center', cellType: 'indicator', indicatorKey: 'requeuedAfterPark' },
    queueTableColumn({ key: 'firstAlarmDetected', label: 'First Alarm Detected', grow: 1.4 }),
    queueTableColumn({ key: 'location', label: 'Location/Event', grow: 2.2 }),
    queueTableColumn({ key: 'priority', label: 'Priority', grow: 0.8 }),
    queueTableColumn({ key: 'alarmCount', label: 'Alarm Count', grow: 0.8 }),
  ];

  private readonly queueSource = resource({
    params: () => ({
      sort: this.sort(),
      revision: this.eventHandles.handlesRevision(),
      sync: this.dataSync.revisionFor('queue'),
    }),
    loader: ({ params }) =>
      firstValueFrom(this.eventsService.getEventsQueue(params.sort ?? undefined)),
  });

  readonly queue = withPreviousResourceValue(this.queueSource);
  readonly totalCount = computed(() => this.queue.value()?.totalCount ?? 0);
  readonly queueRows = computed((): EventqDataTableRow[] =>
    (this.queue.value()?.items ?? []).map(
      (item): EventqDataTableRow => ({
        ...item,
        requeuedIndicator: '',
        requeuedAfterPark: isRequeuedAfterPark(item.priority),
        priority: displayQueuePriority(item.priority),
      }),
    ),
  );

  constructor() {
    this.dataSync.watchWhileAlive('queue');
    autoReloadResource(this.queueSource, environment.eventRefreshIntervalMs);
  }

  onSort(sort: EventqSortState): void {
    const next = activeQueueSort(sort);
    if (!next) {
      return;
    }
    this.browserTabs.updateActivePageState({ eventsSort: next });
  }

  onRowClick(row: EventqDataTableRow): void {
    const eventId = String(row['id'] ?? '');
    if (!eventId || this.eventHandles.blockedOpenEventIds().includes(eventId)) {
      return;
    }
    this.browserTabs.updateActivePageState({ selectedEventId: eventId });
    this.eventRowActivate.emit(eventId);
  }

  selectFirstEvent(): void {
    const first = this.queue.value()?.items[0];
    if (first) {
      this.browserTabs.updateActivePageState({ selectedEventId: first.id });
    }
  }
}
